<template>
    <main class="news-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/news" class="tbv2-crumb">{{ $t('news.page.title') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ $t('news.categories.page_title') }}</span>
            </div>
        </portal>

        <header class="news-hero">
            <router-link to="/news" class="news-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') }}</span>
            </router-link>
            <div class="news-hero-row">
                <div>
                    <h1 class="news-hero-title">
                        {{ $t('news.categories.page_title') }}
                    </h1>
                    <p class="news-hero-sub">
                        {{ $t('news.categories.page_subtitle') }}
                    </p>
                </div>
                <BmvButton
                    v-if="canCreate"
                    variant="primary"
                    icon="fal fa-plus"
                    @click="openCreate"
                >
                    {{ $t('news.categories.new') }}
                </BmvButton>
            </div>
        </header>

        <section class="nc-card">
            <div v-if="isLoading" class="nc-state">
                <div class="spinner-border text-primary" role="status"></div>
            </div>

            <div v-else-if="!categoriesList.length" class="nc-state nc-empty">
                <i class="fal fa-tags"></i>
                <p>{{ $t('news.categories.empty') }}</p>
            </div>

            <ul v-else class="nc-list">
                <li
                    v-for="cat in categoriesList"
                    :key="cat.id"
                    class="nc-row"
                >
                    <div class="nc-row-body">
                        <h3 class="nc-row-title">{{ displayName(cat, 'fr') }}</h3>
                        <p v-if="displayName(cat, 'en')" class="nc-row-sub">
                            <span class="nc-lang">EN</span>
                            {{ displayName(cat, 'en') }}
                        </p>
                    </div>
                    <span
                        class="nc-status"
                        :class="cat.active ? 'nc-status--on' : 'nc-status--off'"
                    >
                        <i
                            :class="cat.active ? 'fal fa-check-circle' : 'fal fa-pause-circle'"
                        ></i>
                        {{ cat.active
                            ? $t('news.categories.active')
                            : $t('news.categories.inactive') }}
                    </span>
                    <div class="nc-row-actions">
                        <BmvButton
                            v-if="canUpdate"
                            variant="ghost"
                            size="sm"
                            icon="fal fa-pen"
                            :title="$t('common.edit')"
                            @click="openEdit(cat)"
                        />
                        <BmvButton
                            v-if="canDelete"
                            variant="danger"
                            size="sm"
                            icon="fal fa-trash"
                            :title="$t('common.delete')"
                            @click="confirmDelete(cat)"
                        />
                    </div>
                </li>
            </ul>
        </section>

        <BmvModal
            v-model="modalOpen"
            size="md"
            :closable="!isSaving"
            :close-on-overlay="!isSaving"
            :close-on-esc="!isSaving"
            @close="cancelModal"
        >
            <template #header>
                <h2 class="nc-modal-title">
                    {{ editing && editing.id
                        ? $t('news.categories.form_title_edit')
                        : $t('news.categories.form_title_create') }}
                </h2>
            </template>

            <div v-if="editing" class="nc-modal-body">
                <div class="nc-field">
                    <label class="nc-field-label">
                        {{ $t('news.categories.name') }}
                        <span class="nc-field-lang">FR</span>
                    </label>
                    <input
                        v-model.trim="editing.translations.fr"
                        type="text"
                        class="nc-input"
                        :placeholder="$t('news.categories.name')"
                    />
                </div>

                <div class="nc-field">
                    <label class="nc-field-label">
                        {{ $t('news.categories.name') }}
                        <span class="nc-field-lang">EN</span>
                    </label>
                    <input
                        v-model.trim="editing.translations.en"
                        type="text"
                        class="nc-input"
                        :placeholder="$t('news.categories.name')"
                    />
                </div>

                <label class="nc-toggle-row">
                    <span class="nc-toggle-label">
                        {{ $t('news.categories.toggle_active') }}
                    </span>
                    <span class="nc-switch">
                        <input type="checkbox" v-model="editing.active" />
                        <span class="nc-switch-track"></span>
                    </span>
                </label>
            </div>

            <template #footer>
                <BmvButton
                    variant="ghost"
                    size="md"
                    :disabled="isSaving"
                    @click="cancelModal"
                >
                    {{ $t('common.cancel') }}
                </BmvButton>
                <BmvButton
                    variant="primary"
                    size="md"
                    icon="fal fa-save"
                    :loading="isSaving"
                    :disabled="!canSave"
                    @click="save"
                >
                    {{ $t('common.save') }}
                </BmvButton>
            </template>
        </BmvModal>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvModal from '@/components/UI/v2/BmvModal.vue'

function emptyCategory() {
    return {
        id: null,
        active: true,
        translations: { fr: '', en: '' }
    }
}

function toFormState(cat) {
    const translations = { fr: '', en: '' }
    if (Array.isArray(cat.translations)) {
        cat.translations.forEach((t) => {
            if (t && t.locale) {
                translations[t.locale] = t.name || t.title || ''
            }
        })
    } else if (cat.translations && typeof cat.translations === 'object') {
        translations.fr = cat.translations.fr || ''
        translations.en = cat.translations.en || ''
    }
    return {
        id: cat.id,
        active: cat.active !== false,
        translations
    }
}

export default {
    name: 'NewsCategories',
    components: { BmvButton, BmvModal },
    data() {
        return {
            isLoading: false,
            modalOpen: false,
            editing: null,
            isSaving: false
        }
    },
    computed: {
        ...mapState('news', {
            categories: (state) => state.categories
        }),
        categoriesList() {
            if (!this.categories) return []
            return Array.isArray(this.categories)
                ? this.categories
                : this.categories.data || []
        },
        canSave() {
            return (
                this.editing &&
                this.editing.translations &&
                (this.editing.translations.fr || '').trim().length > 0
            )
        },
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canCreate() {
            return this.permissions.includes('news-categories.create')
        },
        canUpdate() {
            return this.permissions.includes('news-categories.update')
        },
        canDelete() {
            return this.permissions.includes('news-categories.delete')
        }
    },
    created() {
        this.loadCategories()
    },
    methods: {
        ...mapActions('news', [
            'getCategories',
            'createCategory',
            'updateCategory',
            'deleteCategory'
        ]),
        displayName(cat, locale) {
            if (!cat || !cat.translations) return ''
            if (Array.isArray(cat.translations)) {
                const match = cat.translations.find((t) => t.locale === locale)
                return (match && (match.name || match.title)) || ''
            }
            return cat.translations[locale] || ''
        },
        loadCategories() {
            this.isLoading = true
            return this.getCategories()
                .catch((err) => this.$store.dispatch('dispatchError', err))
                .finally(() => {
                    this.isLoading = false
                })
        },
        openCreate() {
            this.editing = emptyCategory()
            this.modalOpen = true
        },
        openEdit(cat) {
            this.editing = toFormState(cat)
            this.modalOpen = true
        },
        cancelModal() {
            if (this.isSaving) return
            this.modalOpen = false
            this.editing = null
        },
        async save() {
            if (!this.canSave || this.isSaving) return
            this.isSaving = true
            const payload = {
                fr: { name: this.editing.translations.fr },
                active: !!this.editing.active
            }
            const enName = (this.editing.translations.en || '').trim()
            if (enName) payload.en = { name: enName }
            try {
                if (this.editing.id) {
                    await this.updateCategory({ id: this.editing.id, payload })
                } else {
                    await this.createCategory(payload)
                }
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('common.save')
                })
                this.modalOpen = false
                this.editing = null
                await this.loadCategories()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.isSaving = false
            }
        },
        async confirmDelete(cat) {
            const dialog = this.$bmvDialog
            const ok =
                dialog && typeof dialog.confirm === 'function'
                    ? await dialog.confirm({
                          variant: 'danger',
                          title: this.$t('news.categories.delete_confirm'),
                          confirmLabel: this.$t('common.delete'),
                          cancelLabel: this.$t('common.cancel')
                      })
                    : window.confirm(this.$t('news.categories.delete_confirm'))
            if (!ok) return
            try {
                await this.deleteCategory(cat.id)
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('common.delete')
                })
                await this.loadCategories()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            }
        }
    }
}
</script>

<style scoped>
.news-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.news-hero {
    margin-bottom: var(--v2-space-6);
}

.news-hero-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}

.news-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.news-back:hover {
    color: var(--v2-primary);
}

.news-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.news-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

/* Card / list */
.nc-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    box-shadow: var(--v2-shadow-sm);
}

.nc-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 80px 24px;
    color: var(--v2-text-3);
}

.nc-empty i {
    font-size: 2rem;
    color: var(--v2-primary);
}

.nc-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.nc-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 20px;
    border-bottom: 1px solid var(--v2-border-subtle);
}

.nc-row:last-child {
    border-bottom: 0;
}

.nc-row-body {
    flex: 1;
    min-width: 0;
}

.nc-row-title {
    margin: 0 0 2px;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--v2-text-1);
}

.nc-row-sub {
    margin: 0;
    font-size: 0.78rem;
    color: var(--v2-text-3);
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.nc-lang {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 4px;
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.nc-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.nc-status i {
    font-size: 0.7rem;
}

.nc-status--on {
    background: var(--v2-success-soft);
    color: var(--v2-success);
}

.nc-status--off {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.nc-row-actions {
    display: inline-flex;
    gap: 6px;
}

/* Modal */
.nc-modal-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--v2-text-1);
}

.nc-modal-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.nc-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.nc-field-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v2-text-3);
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.nc-field-lang {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 4px;
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.nc-input {
    height: 38px;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    font-size: 0.875rem;
    transition: border-color var(--v2-transition-fast);
}

.nc-input:focus {
    outline: none;
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-focus);
}

/* Toggle switch */
.nc-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    margin: 0;
    border-top: 1px solid var(--v2-border-subtle);
    cursor: pointer;
    user-select: none;
}

.nc-toggle-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--v2-text-1);
}

.nc-switch {
    position: relative;
    width: 36px;
    height: 20px;
    flex-shrink: 0;
}

.nc-switch input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
}

.nc-switch-track {
    position: absolute;
    inset: 0;
    background: var(--v2-surface-3);
    border-radius: 999px;
    transition: background var(--v2-transition-fast);
}

.nc-switch-track::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.25);
    transition: transform var(--v2-transition-fast);
}

.nc-switch input:checked + .nc-switch-track {
    background: var(--v2-primary);
}

.nc-switch input:checked + .nc-switch-track::after {
    transform: translateX(16px);
}

@media (max-width: 900px) {
    .news-v2 {
        padding: 16px 18px 32px;
    }

    .nc-row {
        flex-wrap: wrap;
    }
}
</style>
